import type { CrosstabDataResponse } from '@/lib/crosstab/data-types';
import { respondentUnitNote, type EntryProfile } from '@/lib/crosstab/respondent-unit';

/** M4S-5777: the data routes also say how many diary entries the counted people made. */
type WithEntryProfile = { entryProfile?: EntryProfile };

/**
 * Builds a minimal crosstab context object safe to embed in a DANI prompt.
 *
 * The plan-mode/chat endpoint enforces a 10,000-character message limit.
 * This function fits as many questions as possible within `maxChars` (the
 * budget for the serialised context object itself — the caller must account
 * for the outer JSON wrapper on top of this).
 *
 * Questions are included in order; if the survey has more questions than
 * fit, `note` records how many were omitted so DANI is aware.
 */
export function buildCrosstabContext(
    data: Omit<CrosstabDataResponse, 'responses'> & WithEntryProfile,
    maxChars = 5500,
): Record<string, unknown> {
    // On a diary study the count is people, and DANI is told so (X20).
    const respondentUnit = respondentUnitNote(data.entryProfile);
    const base = {
        projectId: data.projectId,
        surveyIds: data.surveyIds,
        totalRespondents: data.totalRespondents,
        ...(respondentUnit ? { respondentUnit } : {}),
        totalQuestions: data.questions.length,
    };

    const allQuestions = (data.questions ?? []).map((q) => ({
        id: q.id,
        question: q.question,
        options: (q.options ?? []).map((o) => o.option),
    }));

    // Greedily add questions until we'd exceed the char budget.
    const included: typeof allQuestions = [];
    // Seed with the base object size (questions array empty).
    let usedChars = JSON.stringify({ ...base, questions: [] }).length;

    for (const q of allQuestions) {
        const qLen = JSON.stringify(q).length + 1; // +1 for separating comma
        if (usedChars + qLen > maxChars) break;
        included.push(q);
        usedChars += qLen;
    }

    const result: Record<string, unknown> = { ...base, questions: included };
    if (included.length < allQuestions.length) {
        result.note = `Showing ${included.length} of ${allQuestions.length} questions (truncated to fit message size limit)`;
    }
    return result;
}

/**
 * Builds a compact markdown crosstab context for the Survey Analysis template.
 *
 * `buildCrosstabContext` above sends a question *inventory* — ids, text, and
 * option labels, no counts. That is enough for iOOH, where the numbers come
 * from the parsed workbook, but Survey Analysis has no other source: its whole
 * job is to topline and cross the study, so the payload has to carry the
 * counts themselves.
 *
 * Markdown rather than JSON because the same information costs roughly a third
 * fewer characters without braces and quoting, and the 10,000-character
 * plan-mode message limit is the binding constraint. `maxChars` defaults to
 * ~8.5KB, leaving room for the prompt wrapper.
 *
 * Truncation is explicit: questions are included in full or not at all, and a
 * trailing note records what was dropped so the model never silently reports
 * on a partial study as though it were complete.
 */
export function buildSurveyAnalysisContext(
    data: Omit<CrosstabDataResponse, 'responses' | 'questions'> & {
        questions: (CrosstabDataResponse['questions'][number] & {
            /** Per-option counts, when the caller has computed them. */
            counts?: Record<string, number>;
            /** Respondents who answered this question. */
            base?: number;
        })[];
    },
    maxChars = 8500,
): string {
    const header = [
        `# Study payload`,
        `- project: ${data.projectId}`,
        `- surveys: ${data.surveyIds.join(', ') || '—'}`,
        `- total respondents: ${data.totalRespondents}`,
        `- questions: ${data.questions.length}`,
        '',
    ].join('\n');

    const blocks = data.questions.map((q) => {
        const base = q.base ?? data.totalRespondents;
        const lines = [`## ${q.qid} — ${q.question}`, `type: ${q.questionType} · base n=${base}`];
        for (const option of q.options ?? []) {
            const count = q.counts?.[option.option];
            if (count == null) {
                lines.push(`- ${option.option}`);
            } else {
                const pct = base > 0 ? Math.round((count / base) * 100) : 0;
                lines.push(`- ${option.option}: ${count} (${pct}%)`);
            }
        }
        return `${lines.join('\n')}\n`;
    });

    const included: string[] = [];
    let used = header.length;
    for (const block of blocks) {
        if (used + block.length > maxChars) break;
        included.push(block);
        used += block.length;
    }

    const omitted = blocks.length - included.length;
    const note =
        omitted > 0
            ? `\n> NOTE: ${included.length} of ${blocks.length} questions are included; ${omitted} were omitted to fit the message size limit. Do not describe this study as fully analysed.\n`
            : '';

    return `${header}${included.join('\n')}${note}`;
}
